/**
 * Ficha del paciente (terapeuta): datos, registro del consentimiento del tutor
 * (para pacientes propios sin padre), historial cronológico de evaluaciones y
 * detalle clínico completo de cada una (perfil, ítems con transcripción ASR,
 * flags y audio con URL firmada).
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { childrenApi, bateriaApi } from "../../api/endpoints.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Badge, type BadgeTone } from "../../components/Badge.js";
import { SharedAudio, PlayButton } from "../../components/AudioPlayer.js";
import { ErrorBanner } from "../../components/Form.js";
import { Loader } from "../../components/Loader.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import { useSignedAudioPlayer } from "../../hooks/useSignedAudioPlayer.js";
import { useAuth } from "../../auth/AuthContext.js";
import { edadEnAnios } from "../../utils/edad.js";
import type { BateriaCompletaDTO, EstadoBateria } from "../../api/types.js";
import s from "./PatientDetailPage.module.css";

/** Etiqueta + tono del badge de estado (consistente con la vista Evaluaciones). */
const ESTADO: Partial<Record<EstadoBateria, { label: string; tono: BadgeTone }>> = {
  completada: { label: "Pendiente de procesar", tono: "amber" },
  procesando: { label: "Procesando", tono: "amber" },
  procesada: { label: "Procesada", tono: "green" },
  inconcluyente: { label: "Inconcluyente", tono: "red" },
  cancelada: { label: "Cancelada", tono: "neutral" },
  suspendida: { label: "Suspendida", tono: "neutral" },
  en_progreso: { label: "En progreso", tono: "teal" },
};


export function PatientDetailPage() {
  const { childId = "" } = useParams();
  const { usuario } = useAuth();
  const nino = useQuery(() => childrenApi.obtener(childId), [childId]);
  const consent = useQuery(() => childrenApi.consentimientos(childId), [childId]);
  const historial = useQuery(() => bateriaApi.listar(childId), [childId]);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [procesandoId, setProcesandoId] = useState<string | null>(null);
  const [procError, setProcError] = useState<string | null>(null);

  const esPropio = nino.data?.padreId === null && nino.data?.createdBy === usuario?.id;

  // Procesar una batería 'completada' atascada (consume 1 crédito, idempotente).
  async function procesar(id: string) {
    setProcError(null);
    setProcesandoId(id);
    try {
      await bateriaApi.procesar(id);
      historial.reload();
    } catch (e) {
      setProcError(mensajeDeError(e));
    } finally {
      setProcesandoId(null);
    }
  }

  return (
    <>
      <ScreenHeader title="Ficha del paciente" back="/terapeuta/pacientes" />
      <Content>
        {nino.loading ? <Loader /> : null}
        {nino.error ? <ErrorBanner message={nino.error} /> : null}
        {nino.data ? (
          <Card>
            <h2 style={{ margin: 0 }}>{nino.data.nombre}</h2>
            <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
              {edadEnAnios(nino.data.fechaNacimiento)} años · {nino.data.sexo ?? "—"}
              {nino.data.padreId === null ? " · paciente propio" : " · vinculado a un padre"}
            </div>
            {esPropio && consent.data?.audioDeclaradoEn ? (
              <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 4 }}>
                Autorización del tutor declarada el{" "}
                {new Date(consent.data.audioDeclaradoEn).toLocaleDateString("es-PE")}.
              </div>
            ) : null}
            {nino.data.notas ? <p style={{ marginTop: 8 }}>{nino.data.notas}</p> : null}
            <div style={{ marginTop: 10 }}>
              <Link to={`/terapeuta/paciente/${childId}/evaluar`}>
                <Button variant="primary">Evaluar ahora</Button>
              </Link>
            </div>
          </Card>
        ) : null}

        <Card>
          <h2>Historial de evaluaciones</h2>
          {historial.loading ? <Loader /> : null}
          {historial.error ? <ErrorBanner message={historial.error} /> : null}
          {procError ? <ErrorBanner message={procError} /> : null}
          {historial.data && historial.data.length === 0 ? (
            <p>Aún no hay evaluaciones para este paciente.</p>
          ) : null}
          {historial.data?.map((b) => {
            const est = ESTADO[b.estado];
            return (
              <div key={b.id} className={s.evalRow}>
                <div className={s.evalTop}>
                  <div className={s.evalMeta}>
                    <strong>{new Date(b.createdAt).toLocaleDateString("es-PE")}</strong>
                    <span className={s.evalBadges}>
                      <Badge tone="neutral">Banda {b.banda}</Badge>
                      {est ? <Badge tone={est.tono}>{est.label}</Badge> : null}
                      {b.modoPrueba ? <Badge tone="red">QA</Badge> : null}
                      {b.profile ? <Badge tone="purple">{b.profile}</Badge> : null}
                    </span>
                  </div>
                  <div className={s.evalActions}>
                    {b.estado === "procesada" || b.estado === "inconcluyente" ? (
                      <Link to={`/terapeuta/revision/${b.id}`}>
                        <Button variant="primary" size="sm">
                          Revisar
                        </Button>
                      </Link>
                    ) : null}
                    {b.estado === "completada" ? (
                      <Button
                        variant="primary"
                        size="sm"
                        loading={procesandoId === b.id}
                        onClick={() => void procesar(b.id)}
                      >
                        {procesandoId === b.id ? "Procesando…" : "Procesar · 1 crédito"}
                      </Button>
                    ) : null}
                    <Button variant="ghost" size="sm" onClick={() => setAbierta(abierta === b.id ? null : b.id)}>
                      {abierta === b.id ? "Cerrar" : "Vista rápida"}
                    </Button>
                  </div>
                </div>
                {abierta === b.id ? <EvalDetail bateriaId={b.id} /> : null}
              </div>
            );
          })}
        </Card>
      </Content>
    </>
  );
}

function EvalDetail({ bateriaId }: { bateriaId: string }) {
  const q = useQuery(() => bateriaApi.obtener(bateriaId), [bateriaId]);
  const b = q.data as BateriaCompletaDTO | null;
  const items = b?.items ?? [];
  // Player unificado: un solo <audio> por foco. Consulta puntual → SIN auto-avance.
  // URL firmada vencida → recarga el detalle (re-firma), nunca abre pestaña ni falla.
  const audioItems = useMemo(
    () => items.map((it) => ({ id: it.id, audioUrl: it.audioUrl ?? null })),
    [items],
  );
  const player = useSignedAudioPlayer({
    items: audioItems,
    onExpired: () => q.reload(),
  });
  if (q.loading) return <Loader />;
  if (q.error) return <ErrorBanner message={q.error} />;
  if (!b) return null;
  return (
    <div style={{ marginTop: 8, fontSize: 13 }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--ink-soft)" }}>
              <th>Ítem</th>
              <th>Objetivo</th>
              <th>Producido (ASR)</th>
              <th>Flags</th>
              <th>Audio</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} style={{ borderTop: "1px solid var(--line)" }}>
                <td>{it.ref}</td>
                <td>{(it.targetPhonemes ?? []).join(" ")}</td>
                <td>{(it.fonemasProducidos ?? []).join(" ") || "—"}</td>
                <td>
                  {[
                    it.noEvoca ? "NO_EVOCA" : "",
                    it.revisarAudio ? "REVISAR" : "",
                    it.dudoso ? "DUDOSO" : "",
                    it.sinIntento ? "SIN_INTENTO" : "",
                  ]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </td>
                <td>{it.audioUrl ? <PlayButton player={player} id={it.id} /> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Único <audio> compartido de la vista rápida (invisible; lo dirigen los ▶). */}
      <SharedAudio player={player} />
      {b.peffReport ? (
        <details style={{ marginTop: 8 }}>
          <summary>Pre-informe completo (M1)</summary>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{JSON.stringify(b.peffReport, null, 2)}</pre>
        </details>
      ) : null}
    </div>
  );
}
